import React from 'react';
import PropTypes from 'prop-types';

/**
 * Section Component
 * 
 * A semantic section container for organizing page content.
 * Provides consistent spacing, optional backgrounds, and title/description.
 * 
 * @example
 * // Basic section
 * <Section title="Features" description="Explore our amazing features">
 *   <div>Feature cards...</div>
 * </Section>
 * 
 * @example
 * // Section with colored background
 * <Section 
 *   title="Testimonials"
 *   background="gray"
 *   padding="xl"
 * >
 *   <div>Testimonial cards...</div>
 * </Section>
 */
const Section = ({
  children,
  title,
  description,
  background = 'transparent',
  padding = 'lg',
  className = '',
  as: Component = 'section',
  ...props
}) => {
  // Background classes
  const backgroundClasses = {
    transparent: '',
    white: 'bg-white',
    gray: 'bg-gray-50',
    blue: 'bg-blue-50',
    primary: 'bg-blue-600 text-white',
  };
  
  // Padding classes
  const paddingClasses = {
    none: '',
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-16',
    xl: 'py-24',
  };
  
  // Combine all classes
  const combinedClasses = [
    backgroundClasses[background],
    paddingClasses[padding],
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <Component className={combinedClasses} {...props}>
      {/* Section Header */}
      {(title || description) && (
        <div className="mb-8 text-center">
          {title && (
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3">
              {title}
            </h2>
          )}
          {description && (
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {description}
            </p>
          )}
        </div>
      )}
      
      {/* Section Content */}
      <div>
        {children}
      </div>
    </Component>
  );
};

Section.propTypes = {
  /** Section content */
  children: PropTypes.node.isRequired,
  /** Section title */
  title: PropTypes.string,
  /** Section description */
  description: PropTypes.string,
  /** Background color variant */
  background: PropTypes.oneOf(['transparent', 'white', 'gray', 'blue', 'primary']),
  /** Vertical padding size */
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** HTML element or React component to render as */
  as: PropTypes.elementType,
};

export default Section;
